import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Rocket } from "lucide-react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { triggerProjectRerun } from "@/lib/project-rerun.functions";
import { checkRunNulls, type NullCheckReport } from "@/lib/decision-null-check.functions";
import { NullCheckPanel } from "@/components/portal/NullCheckPanel";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle } from "lucide-react";
import { ProjectAnalysisPanel } from "@/components/portal/ProjectAnalysisPanel";
import { DecisionRunStatus, mutationStatus } from "@/components/portal/DecisionRunStatus";
import { ProjectLocationRoofForm } from "@/components/portal/ProjectLocationRoofForm";
import { ProjectVorhabenForm } from "@/components/portal/ProjectVorhabenForm";
import { ProjectOverview } from "@/components/portal/ProjectOverview";
import { MieterEnergieverkaufForm } from "@/components/portal/MieterEnergieverkaufForm";
import { ProjectDescriptionDocument } from "@/components/portal/ProjectDescriptionDocument";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

const STATUS = ["entwurf", "analyse", "angebot_phase1", "angebot_phase2", "beauftragt", "abgeschlossen", "verloren", "archiviert"];

export const Route = createFileRoute("/_authenticated/app/crm/projects/$id")({
  ssr: false,
  head: () => ({ meta: [{ title: "Projektakte" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ProjectDetail,
});

function ProjectDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const idLooksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);

  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      let query = supabase
        .from("projects")
        .select("*, customers(id, customer_no, first_name, last_name, company, email)");
      query = idLooksLikeUuid ? query.eq("id", id) : query.eq("project_no", id);
      const { data, error } = await query.single();
      if (error) throw error;
      return data;
    },
  });
  const projectId = project?.id as string | undefined;

  const { data: notes } = useQuery({
    queryKey: ["project-notes", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase.from("project_notes").select("*").eq("project_id", projectId!).order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: tasks } = useQuery({
    queryKey: ["project-tasks", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase.from("project_tasks").select("*").eq("project_id", projectId!).order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: files } = useQuery({
    queryKey: ["project-files", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase.from("project_files").select("*").eq("project_id", projectId!).order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const [noteBody, setNoteBody] = useState("");
  const addNote = useMutation({
    mutationFn: async () => {
      if (!project) return;
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("project_notes").insert({
        tenant_id: project.tenant_id, project_id: projectId!, author_id: u.user!.id, body: noteBody,
      });
      if (error) throw error;
    },
    onSuccess: () => { setNoteBody(""); qc.invalidateQueries({ queryKey: ["project-notes", projectId] }); },
  });

  const [taskForm, setTaskForm] = useState({ title: "", description: "", priority: "normal" });
  const addTask = useMutation({
    mutationFn: async () => {
      if (!project || !taskForm.title) return;
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("project_tasks").insert({
        tenant_id: project.tenant_id, project_id: projectId!, created_by: u.user!.id, ...taskForm,
      });
      if (error) throw error;
    },
    onSuccess: () => { setTaskForm({ title: "", description: "", priority: "normal" }); qc.invalidateQueries({ queryKey: ["project-tasks", projectId] }); },
  });

  const setStatus = useMutation({
    mutationFn: async (status: string) => {
      const { error } = await supabase.from("projects").update({ status }).eq("id", projectId!);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", id] }),
  });

  const checkNullsFn = useServerFn(checkRunNulls);
  const [phase2, setPhase2] = useState(false);
  const [nullReport, setNullReport] = useState<NullCheckReport | null>(null);
  const [nullLoading, setNullLoading] = useState(false);
  const [nullError, setNullError] = useState<string | null>(null);


  // Sicherer Server-Endpoint: Re-Run via project_no, nur interne Mitarbeiter
  const triggerRerunFn = useServerFn(triggerProjectRerun);

  // Interne-Rolle-Check (für sichtbaren Hauptbutton)
  const { data: isInternal } = useQuery({
    queryKey: ["is-internal-staff", project?.tenant_id],
    enabled: !!project?.tenant_id,
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return false;
      const { data, error } = await supabase.rpc("is_internal_staff", {
        _user_id: u.user.id,
        _tenant_id: project!.tenant_id as string,
      });
      if (error) return false;
      return !!data;
    },
  });

  const secureRerun = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Nicht eingeloggt – bitte erneut anmelden.");
      if (!project) throw new Error("Projekt nicht gefunden.");
      return triggerRerunFn({
        data: {
          project_no: project?.project_no as string | undefined,
          project_id: projectId,
          phase_type: phase2 ? "phase2" : "phase1",
          trigger_source: "crm_secure_button",
        },
      });
    },
    onSuccess: async (res: any) => {
      toast.success("Decision Run gestartet", {
        description: `${res.project_no} · Run-ID ${String(res.run_id).slice(0, 8)}… · ${res.variant_count} Varianten`,
      });
      qc.invalidateQueries({ queryKey: ["decision-runs", projectId] });
      qc.invalidateQueries({ queryKey: ["project", id] });
      if (res?.run_id) {
        navigate({ to: "/app/crm/projects/$id/runs/$runId", params: { id: res.project_no, runId: res.run_id } });
        // NULL-Check im Hintergrund
        setNullLoading(true);
        setNullError(null);
        setNullReport(null);
        try {
          const report = await checkNullsFn({ data: { run_id: res.run_id } });
          setNullReport(report);
          if (!report.ok) {
            toast.warning("NULL-Prüfung", {
              description: `${report.total_missing} Pflichtfelder noch leer.`,
            });
          }
        } catch (e: any) {
          setNullError(e?.message ?? "Unbekannter Fehler");
        } finally {
          setNullLoading(false);
        }
      }
    },
    onError: (err: any) => {
      const raw = err?.message ?? String(err);
      let friendly = raw;
      if (/Unauthorized|No authorization|not authenticated/i.test(raw)) {
        friendly = "Nicht eingeloggt – bitte erneut anmelden und Seite neu laden.";
      } else if (/internen Mitarbeitern vorbehalten|Kein Zugriff/i.test(raw)) {
        friendly = "Keine Berechtigung: Decision Run ist internen Mitarbeitern (Admin/Manager/Vertrieb/Experte) vorbehalten.";
      } else if (/Projekt nicht gefunden/i.test(raw)) {
        friendly = "Projekt nicht gefunden – evtl. falsche ID oder kein Zugriff (RLS).";
      } else if (/row-level security|permission denied/i.test(raw)) {
        friendly = "Zugriffsproblem (RLS/Tenant). Bitte interne Rolle und Tenant-Zuordnung prüfen.";
      }
      toast.error("Decision Run fehlgeschlagen", { description: friendly });
    },
  });

  if (!project) return <PortalShell nav={NAV}><p>Lade …</p></PortalShell>;
  const c = (project as unknown as { customers: { id: string; customer_no: string; first_name: string | null; last_name: string | null; company: string | null; email: string | null } | null }).customers;
  const cname = c ? (c.company || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim()) : "—";

  return (
    <PortalShell nav={NAV}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{project.name}</h1>
          <p className="text-sm text-muted-foreground font-mono">{project.project_no}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={project.status} onValueChange={(v) => setStatus.mutate(v)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Link to="/app/decision/$projectId" params={{ projectId: projectId! }}>
            <Button variant="secondary" size="sm">Decision Center</Button>
          </Link>
          {isInternal && (
            <Button
              size="lg"
              disabled={secureRerun.isPending}
              onClick={() => secureRerun.mutate()}
              className="shadow-md"
              title="Startet den Decision Run serverseitig mit Rollenprüfung (nur interne Mitarbeiter)."
            >
              <Rocket className="h-4 w-4 mr-2" />
              {secureRerun.isPending
                ? "Run wird gestartet…"
                : `Decision Run jetzt starten${phase2 ? " (Phase 2)" : ""}`}
            </Button>
          )}
          {isInternal === false && (
            <span className="text-xs text-muted-foreground">
              Decision Run nur für interne Mitarbeiter sichtbar.
            </span>
          )}
          <Link to="/app/crm/projects"><Button variant="outline" size="sm">Zurück</Button></Link>
        </div>
      </div>



      <div className="mb-4">
        <DecisionRunStatus
          status={mutationStatus(secureRerun)}
          runId={(secureRerun.data as any)?.run_id}
          variantCount={(secureRerun.data as any)?.variant_count}
          errorMessage={(secureRerun.error as any)?.message}
          onRetry={secureRerun.isError ? () => secureRerun.mutate() : undefined}
        />
        {(secureRerun.data as any)?.run_id && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Neue Run-ID:</span>
            <code className="font-mono text-xs bg-muted px-2 py-0.5 rounded break-all">
              {(secureRerun.data as any).run_id}
            </code>
            <Link
              to="/app/crm/projects/$id/runs/$runId"
              params={{ id: project.project_no as string, runId: (secureRerun.data as any).run_id }}
              className="underline text-muted-foreground hover:text-foreground"
            >
              Run-Details öffnen →
            </Link>
          </div>
        )}
        {(nullLoading || nullError || nullReport) && (
          <div className="mt-2">
            <NullCheckPanel report={nullReport} loading={nullLoading} error={nullError} />
          </div>
        )}
      </div>

      <Card className="mb-6 border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/10">
        <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Switch checked={phase2} onCheckedChange={setPhase2} id="phase2-toggle" />
            <Label htmlFor="phase2-toggle" className="cursor-pointer">Phase 2 aktivieren</Label>
          </div>
          <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Phase 2 erfordert Expertenfreigabe und reale Angebots-/Förderdaten. Im Shadow-Mode werden Platzhalter erzeugt — kein Kundenversand.</span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="uebersicht" className="mb-6">
        <TabsList>
          <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
          <TabsTrigger value="vorhaben">Vorhaben</TabsTrigger>
          <TabsTrigger value="standort">Standort &amp; Dach</TabsTrigger>
          <TabsTrigger value="mieter">Mieter &amp; Energieverkauf</TabsTrigger>
          <TabsTrigger value="analyse">Analyseergebnisse</TabsTrigger>
          <TabsTrigger value="dokument">Projektbeschreibung</TabsTrigger>
          <TabsTrigger value="akte">Projektakte</TabsTrigger>
        </TabsList>
        <TabsContent value="uebersicht" className="mt-4">
          <ProjectOverview projectId={projectId!} />
        </TabsContent>
        <TabsContent value="vorhaben" className="mt-4">
          <ProjectVorhabenForm projectId={projectId!} />
        </TabsContent>
        <TabsContent value="standort" className="mt-4">
          <ProjectLocationRoofForm projectId={projectId!} tenantId={project.tenant_id as string} />
        </TabsContent>
        <TabsContent value="mieter" className="mt-4">
          <MieterEnergieverkaufForm projectId={projectId!} />
        </TabsContent>
        <TabsContent value="analyse" className="mt-4">
          <ProjectAnalysisPanel projectId={projectId!} />
        </TabsContent>
        <TabsContent value="dokument" className="mt-4">
          <ProjectDescriptionDocument projectId={projectId!} />
        </TabsContent>
        <TabsContent value="akte" className="mt-4">



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Projektdaten</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Kunde:</span>{" "}
              {c ? <Link to="/app/crm/customers/$id" params={{ id: c.id }} className="underline">{cname}</Link> : "—"}
            </div>
            <div><span className="text-muted-foreground">Gebäudetyp:</span> {project.building_type ?? "—"}</div>
            <div><span className="text-muted-foreground">Adresse:</span> {[project.street, project.house_no].filter(Boolean).join(" ") || "—"}, {[project.postal_code, project.city].filter(Boolean).join(" ")}</div>
            <div><span className="text-muted-foreground">Status:</span> <Badge variant="secondary">{project.status}</Badge></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Notiz hinzufügen</CardTitle></CardHeader>
          <CardContent>
            <Textarea rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Interne Notiz …" />
            <Button className="mt-3" size="sm" disabled={!noteBody || addNote.isPending} onClick={() => addNote.mutate()}>Notiz speichern</Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Notizen</CardTitle></CardHeader>
        <CardContent>
          {(notes ?? []).length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Notizen.</p> : (
            <ul className="space-y-3">
              {notes!.map((n) => (
                <li key={n.id} className="border rounded p-3">
                  <p className="text-sm whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("de-DE")}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Aufgaben</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); addTask.mutate(); }} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="space-y-1 sm:col-span-2"><Label>Titel</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} /></div>
            <div className="space-y-1">
              <Label>Priorität</Label>
              <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="niedrig">Niedrig</SelectItem><SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="hoch">Hoch</SelectItem><SelectItem value="dringend">Dringend</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-3"><Label>Beschreibung</Label><Textarea rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
            <div className="sm:col-span-3"><Button type="submit" size="sm" disabled={!taskForm.title || addTask.isPending}>Aufgabe anlegen</Button></div>
          </form>
          <Table>
            <TableHeader><TableRow><TableHead>Titel</TableHead><TableHead>Priorität</TableHead><TableHead>Status</TableHead><TableHead>Fällig</TableHead></TableRow></TableHeader>
            <TableBody>
              {(tasks ?? []).map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.title}</TableCell><TableCell>{t.priority}</TableCell><TableCell>{t.status}</TableCell>
                  <TableCell>{t.due_at ? new Date(t.due_at).toLocaleDateString("de-DE") : "—"}</TableCell>
                </TableRow>
              ))}
              {(tasks ?? []).length === 0 && <TableRow><TableCell colSpan={4} className="text-muted-foreground text-center py-4">Keine Aufgaben.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Dateien</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">Upload-Funktion folgt in Phase 3 (Analyse-Pipeline).</p>
          <Table>
            <TableHeader><TableRow><TableHead>Datei</TableHead><TableHead>Art</TableHead><TableHead>Größe</TableHead><TableHead>Hochgeladen</TableHead></TableRow></TableHeader>
            <TableBody>
              {(files ?? []).map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-mono text-xs">{f.path}</TableCell>
                  <TableCell>{f.kind ?? "—"}</TableCell>
                  <TableCell>{f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB` : "—"}</TableCell>
                  <TableCell>{new Date(f.created_at).toLocaleDateString("de-DE")}</TableCell>
                </TableRow>
              ))}
              {(files ?? []).length === 0 && <TableRow><TableCell colSpan={4} className="text-muted-foreground text-center py-4">Noch keine Dateien.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}
